import { useEffect, useRef, useState } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { loginRequest } from '@/auth/msalConfig';
import { devMode, devUser } from '@/auth/devMode';
import { useApi } from '@/api/client';
import { AppShell } from '@/components/AppShell';
import { Home } from '@/pages/Home';
import { GetStarted } from '@/pages/GetStarted';
import { Playground } from '@/pages/Playground';
import { FileTasks } from '@/pages/FileTasks';
import { Templates } from '@/pages/Templates';
import { MyUsage } from '@/pages/MyUsage';
import { Approvals } from '@/pages/admin/Approvals';
import { Callers } from '@/pages/admin/Callers';
import { Models } from '@/pages/admin/Models';
import { ModelDetail } from '@/pages/admin/ModelDetail';
import { McpServers } from '@/pages/admin/McpServers';
import { Suppliers } from '@/pages/admin/Suppliers';
import { AdminUsage } from '@/pages/admin/AdminUsage';

export default function App() {
  if (devMode) return <AuthedApp />;
  return <RealAuthGate />;
}

function RealAuthGate() {
  const isAuthed = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const [redirectFailed, setRedirectFailed] = useState<Error | null>(null);
  const started = useRef(false);

  // Everyone here is already on a Key Travel machine with an Entra session, so a "Sign in"
  // button just asks them to confirm something they've already done. Start the redirect as
  // soon as MSAL is idle and we know they aren't signed in.
  useEffect(() => {
    if (isAuthed || started.current) return;
    // InteractionStatus.None — MSAL is not already mid-redirect or handling the response.
    if (inProgress !== 'none') return;

    started.current = true;
    instance.loginRedirect(loginRequest).catch((e: unknown) => {
      // A failed redirect must not leave a blank page: fall back to a manual button with the
      // reason, which is also the only route out if popups/redirects are blocked by policy.
      setRedirectFailed(e instanceof Error ? e : new Error(String(e)));
      started.current = false;
    });
  }, [isAuthed, inProgress, instance]);

  if (isAuthed) return <AuthedApp />;

  if (redirectFailed) {
    return (
      <div className="signin-card">
        <h2>Key Travel AI Gateway</h2>
        <p>We couldn't start sign-in automatically: {redirectFailed.message}</p>
        <button
          className="btn-primary"
          onClick={() => {
            setRedirectFailed(null);
            instance.loginRedirect(loginRequest).catch((e: unknown) =>
              setRedirectFailed(e instanceof Error ? e : new Error(String(e))));
          }}>
          Try again
        </button>
      </div>
    );
  }

  return <div className="placeholder">Signing you in...</div>;
}

function AuthedApp() {
  const api = useApi();
  const { data: me, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    // In dev mode we already know who we are; avoid the round-trip.
    initialData: devMode ? devUser : undefined
  });

  if (isLoading && !me) return <div className="placeholder">Loading...</div>;
  if (error || !me) return <div className="error-banner">Could not load your profile: {(error as Error)?.message}</div>;

  return (
    <>
      <AppShell me={me}>
        {/* Inside the content column, not above the top bar: the sticky bar and sidebar are
            positioned from the top of the viewport, and a strip above them would push both off. */}
        {devMode && <DevModeBanner />}
        <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/playground" element={<Playground />} />
          <Route path="/file-tasks" element={<FileTasks />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/usage" element={<MyUsage />} />
          {me.isAdmin && (
            <>
              <Route path="/admin/approvals" element={<Approvals />} />
              <Route path="/admin/callers" element={<Callers />} />
              <Route path="/admin/models" element={<Models />} />
              <Route path="/admin/models/:id" element={<ModelDetail />} />
              <Route path="/admin/mcp-servers" element={<McpServers />} />
              <Route path="/admin/suppliers" element={<Suppliers />} />
              <Route path="/admin/usage" element={<AdminUsage />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </main>
      </AppShell>
    </>
  );
}

function DevModeBanner() {
  return (
    <div style={{
      background: 'var(--kt-orange-bg)',
      borderBottom: '1px solid var(--kt-orange-border)',
      padding: '8px 24px',
      textAlign: 'center',
      fontSize: '.82rem',
      color: 'var(--kt-grey-700)'
    }}>
      <strong style={{ color: 'var(--kt-orange)' }}>Dev mode</strong>
      &nbsp;&middot;&nbsp; auth bypassed, X-Dev-User header sent to the local API
    </div>
  );
}
